import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaChevronDown } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import { fetchTaskById, fetchTaskStatuses, fetchProjectUsers, updateTask } from "./taskService";
import { fetchUserStories } from "./userStoryService";
import TaskFileAttachments from "./TaskFileAttachments";
import { canPerformActionOnPage, PAGES, ACTIONS } from "../utils/roleAccess";
import "../assets/css/EditCustomer.css";
import "../assets/css/Projects.css";
import "../assets/css/UserStories.css";
import "../assets/css/Tasks.css";
import "../assets/css/Permissions.css";
import "../assets/css/Issues.css";

const TASK_PRIORITIES = ["High", "Medium", "Low"];

function EditTask({ onBack, onTaskUpdated }) {
  const navigate = useNavigate();
  const { id: projectId, subprojectId, userStoryId, taskId } = useParams();
  const hasSubproject = Boolean(subprojectId);
  const role = useMemo(() => {
    try {
      const u = localStorage.getItem("user");
      if (!u) return null;
      const user = JSON.parse(u);
      const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
      return (
        (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
        user.role_name ||
        user.role ||
        null
      );
    } catch {
      return null;
    }
  }, []);
  const canEditTaskFiles = canPerformActionOnPage(PAGES.PROJECTS, ACTIONS.EDIT, role);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showAssigneesDropdown, setShowAssigneesDropdown] = useState(false);
  const [projects, setProjects] = useState([]);
  const [subprojects, setSubprojects] = useState([]);
  const [userStories, setUserStories] = useState([]);
  const [loadingSubprojects, setLoadingSubprojects] = useState(false);
  const [loadingUserStories, setLoadingUserStories] = useState(false);
  const [formData, setFormData] = useState({
    projectId: "",
    subProjectId: "",
    userStoryId: "",
    title: "",
    description: "",
    startDate: "",
    endDate: "",
    status: "Backlog",
    priority: "Medium",
    assignedUserIds: []
  });

  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const statuses = await fetchTaskStatuses();
        setTaskStatuses((Array.isArray(statuses) ? statuses : []).sort((a, b) => (a.Status_Id ?? a.status_id ?? 0) - (b.Status_Id ?? b.status_id ?? 0)));
      } catch (err) {
        console.error("Error fetching task statuses:", err);
      }
    };
    loadStatuses();
  }, []);

  // Fetch projects on mount
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axiosInstance.get("/projects");
        const raw = res.data;
        const arr = Array.isArray(raw) ? raw : raw?.data ?? raw?.projects ?? [];
        setProjects(arr || []);
      } catch (err) {
        console.error("Error fetching projects:", err);
        setProjects([]);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!taskId) return;
      setLoading(true);
      setError("");
      try {
        const task = await fetchTaskById(taskId);
        if (task) {
          const assigned = task.Assigned_Users ?? task.assigned_users ?? [];
          const assignedIds = assigned.map((u) => u.User_Id ?? u.user_id ?? u.id).filter(Boolean);
          const taskProjectId = task.Project_Id ?? task.project_id ?? projectId;
          const taskSubprojectId = task.Sub_Project_Id ?? task.sub_project_id ?? subprojectId;
          const taskStoryId = task.Story_Id ?? task.story_id ?? task.User_Story_Id ?? task.user_story_id ?? userStoryId;
          setFormData({
            projectId: taskProjectId ? String(taskProjectId) : "",
            subProjectId: taskSubprojectId ? String(taskSubprojectId) : "",
            userStoryId: taskStoryId ? String(taskStoryId) : "",
            title: task.Title || task.title || "",
            description: task.Description || task.description || "",
            startDate: task.Start_Date || task.start_date || "",
            endDate: task.End_Date || task.end_date || "",
            status: task.Status || task.status || "Backlog",
            priority: task.Priority || task.priority || "Medium",
            assignedUserIds: assignedIds
          });
        } else {
          setError("Task not found.");
        }
      } catch (err) {
        console.error("Error fetching task:", err);
        setError(err.response?.data?.message || "Failed to load task.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [taskId, projectId, subprojectId, userStoryId]);

  // Fetch subprojects when project changes
  useEffect(() => {
    if (!formData.projectId) {
      setSubprojects([]);
      return;
    }
    let cancelled = false;
    setLoadingSubprojects(true);
    axiosInstance
      .get(`/sub-projects/project/${formData.projectId}`)
      .then((res) => {
        if (cancelled) return;
        const raw = res.data;
        let arr = Array.isArray(raw) ? raw : raw?.sub_projects ?? raw?.data ?? [];
        const mapped = (arr || [])
          .map((sp) => ({
            id: sp.Sub_Project_Id ?? sp.sub_project_id ?? sp.id,
            name: sp.Sub_Project_Name ?? sp.sub_project_name ?? sp.name ?? ""
          }))
          .filter((x) => x.id != null && String(x.name).trim() !== "");
        setSubprojects(mapped);
      })
      .catch(() => { if (!cancelled) setSubprojects([]); })
      .finally(() => { if (!cancelled) setLoadingSubprojects(false); });
    return () => { cancelled = true; };
  }, [formData.projectId]);

  // Fetch user stories when project/subproject changes
  useEffect(() => {
    if (!formData.projectId) {
      setUserStories([]);
      return;
    }
    let cancelled = false;
    setLoadingUserStories(true);
    fetchUserStories(Number(formData.projectId), formData.subProjectId ? Number(formData.subProjectId) : null)
      .then((arr) => {
        if (!cancelled) {
          const mapped = (arr || []).map((s) => ({
            id: s.User_Story_Id ?? s.user_story_id ?? s.id,
            title: s.Title ?? s.title ?? ""
          })).filter((x) => x.id);
          setUserStories(mapped);
        }
      })
      .catch(() => { if (!cancelled) setUserStories([]); })
      .finally(() => { if (!cancelled) setLoadingUserStories(false); });
    return () => { cancelled = true; };
  }, [formData.projectId, formData.subProjectId]);

  // Fetch all users for assignment dropdown (not project-restricted).
  useEffect(() => {
    let cancelled = false;
    setLoadingUsers(true);
    axiosInstance
      .get("/users")
      .then((response) => {
        if (cancelled) return;
        const raw = response?.data;
        const arr = Array.isArray(raw) ? raw : raw?.data ?? raw?.users ?? [];
        const mapped = (arr || [])
          .map((u) => {
            const id = u.User_Id ?? u.user_id ?? u.id;
            const explicitName = u.User_Name ?? u.user_name ?? u.name;
            const fullName = `${u.first_name ?? u.firstName ?? ""} ${u.last_name ?? u.lastName ?? ""}`.trim();
            const name = explicitName || fullName || u.email || "—";
            return { id, name };
          })
          .filter((x) => x.id != null);
        setUsers(mapped);
      })
      .catch(() => {
        if (!cancelled) setUsers([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingUsers(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleProjectChange = (e) => {
    const val = e.target.value;
    setFormData((f) => ({ ...f, projectId: val, subProjectId: "", userStoryId: "" }));
  };
  const handleSubprojectChange = (e) => {
    const val = e.target.value;
    setFormData((f) => ({ ...f, subProjectId: val, userStoryId: "" }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUserToggle = (userId) => {
    setFormData((prev) => {
      const ids = prev.assignedUserIds.includes(userId) ? prev.assignedUserIds.filter((id) => id !== userId) : [...prev.assignedUserIds, userId];
      return { ...prev, assignedUserIds: ids };
    });
  };

  const allUsersSelected = !loadingUsers && users.length > 0 && formData.assignedUserIds.length === users.length;
  const handleToggleSelectAllUsers = () => {
    setFormData((prev) => ({
      ...prev,
      assignedUserIds: allUsersSelected ? [] : users.map((u) => u.id)
    }));
  };

  useEffect(() => {
    const closeDropdown = (e) => {
      if (showAssigneesDropdown && !e.target.closest(".form-group-multi-select")) {
        setShowAssigneesDropdown(false);
      }
    };
    document.addEventListener("click", closeDropdown);
    return () => document.removeEventListener("click", closeDropdown);
  }, [showAssigneesDropdown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const title = (formData.title || "").trim();
    if (!title) {
      setError("Title is required.");
      return;
    }
    if (!formData.projectId) {
      setError("Project is required.");
      return;
    }
    if (!formData.userStoryId) {
      setError("User Story is required.");
      return;
    }
    const todayStr = new Date().toISOString().split("T")[0];
    if (formData.startDate && formData.startDate < todayStr) {
      setError("Start date cannot be in the past.");
      return;
    }
    if (formData.endDate && formData.endDate < todayStr) {
      setError("End date cannot be in the past.");
      return;
    }
    if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
      setError("End date cannot be before start date.");
      return;
    }
    setError("");
    let updatedBy = 1;
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        updatedBy = user.user_id || user.id || 1;
      }
    } catch (err) {
      console.error("Error reading user:", err);
    }
    const selectedStatus = (formData.status || "").trim();
    const matchedStatus = (taskStatuses || []).find(
      (s) => String(s.Status ?? s.status ?? "").trim().toLowerCase() === selectedStatus.toLowerCase()
    );
    const matchedStatusId = Number(matchedStatus?.Status_Id ?? matchedStatus?.status_id);

    const payload = {
      Project_Id: Number(formData.projectId),
      Sub_Project_Id: formData.subProjectId ? Number(formData.subProjectId) : null,
      Story_Id: Number(formData.userStoryId),
      Title: title,
      Description: (formData.description || "").trim(),
      Status: selectedStatus || null,
      ...(Number.isFinite(matchedStatusId) ? { Status_Id: matchedStatusId } : {}),
      Start_Date: formData.startDate || null,
      End_Date: formData.endDate || null,
      Priority: formData.priority || "Medium",
      Updated_By: updatedBy,
      Assigned_User_Ids: (formData.assignedUserIds || []).map((id) => Number(id))
    };
    if (!payload.Start_Date) delete payload.Start_Date;
    if (!payload.End_Date) delete payload.End_Date;

    setSubmitting(true);
    try {
      await updateTask(taskId, payload);
      alert("Task updated successfully.");
      if (onTaskUpdated) onTaskUpdated();
      if (onBack) onBack();
      else if (formData.subProjectId) {
        navigate(`/projects/${formData.projectId}/subprojects/${formData.subProjectId}/user-stories/${formData.userStoryId}/view`);
      } else {
        navigate(`/projects/${formData.projectId}/user-stories/${formData.userStoryId}/view`);
      }
    } catch (err) {
      console.error("Error updating task:", err);
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to update task.");
    } finally {
      setSubmitting(false);
    }
  };

  const goBack = () => {
    if (onBack) onBack();
    else if (formData.subProjectId) {
      navigate(`/projects/${formData.projectId}/subprojects/${formData.subProjectId}/user-stories/${formData.userStoryId}/view`);
    } else if (formData.projectId && formData.userStoryId) {
      navigate(`/projects/${formData.projectId}/user-stories/${formData.userStoryId}/view`);
    } else {
      navigate(-1);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  if (loading) {
    return (
      <div className="project-form-container editcustomer-container">
        <div className="editcustomer-loading">Loading task...</div>
      </div>
    );
  }

  if (error && !formData.title) {
    return (
      <div className="project-form-container editcustomer-container">
        <button type="button" className="viewlead-back-link" onClick={goBack}>
          <FaArrowLeft /> Back
        </button>
        <div className="userstories-add-error" style={{ marginTop: 16 }}>{error}</div>
      </div>
    );
  }

  return (
    <div className="project-form-container editcustomer-container">
      <div className="viewlead-header">
        <button type="button" className="viewlead-back-link" onClick={goBack}>
          <FaArrowLeft /> Back
        </button>
      </div>
      <div className="issues-main-card">
      <form className="editcustomer-form edituserstory-form" onSubmit={handleSubmit}>
        <div className="form-section">
          {error && (
            <div className="userstories-add-error" style={{ marginBottom: 16 }}>
              {error}
            </div>
          )}
          <div className="form-grid addtask-form-grid">
            <div className="form-group">
              <label>Project <span className="required">*</span></label>
              <select
                name="projectId"
                value={formData.projectId}
                onChange={handleProjectChange}
                required
              >
                <option value="">Select project</option>
                {projects.map((p) => (
                  <option key={p.Project_Id ?? p.project_id ?? p.id} value={String(p.Project_Id ?? p.project_id ?? p.id)}>
                    {p.Project_Name ?? p.project_name ?? p.name ?? ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Subproject</label>
              <select
                name="subProjectId"
                value={formData.subProjectId}
                onChange={handleSubprojectChange}
                disabled={loadingSubprojects}
              >
                <option value="">None</option>
                {subprojects.map((sp) => (
                  <option key={sp.id} value={String(sp.id)}>{sp.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>User Story <span className="required">*</span></label>
              <select
                name="userStoryId"
                value={formData.userStoryId}
                onChange={handleInputChange}
                disabled={loadingUserStories}
                required
              >
                <option value="">Select user story</option>
                {userStories.map((s) => (
                  <option key={s.id} value={String(s.id)}>{s.title || `#${s.id}`}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Title <span className="required">*</span></label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
              
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select name="status" value={formData.status} onChange={handleInputChange}>
                {taskStatuses.length === 0 && (
                  <option value="Backlog">Backlog</option>
                )}
                {taskStatuses.map((s) => (
                  <option key={s.Status_Id ?? s.status_id} value={s.Status ?? s.status}>
                    {s.Status ?? s.status}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Priority</label>
              <select name="priority" value={formData.priority} onChange={handleInputChange}>
                {TASK_PRIORITIES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                min={today}
              />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                min={formData.startDate || today}
              />
            </div>
            <div className="form-group form-group-multi-select">
              <label>Assigned Users</label>
              <div style={{ width: "100%" }}>
                <div
                  className="permissions-select-trigger"
                  onClick={() => {
                    if (!loadingUsers) setShowAssigneesDropdown((prev) => !prev);
                  }}
                  style={{
                    opacity: loadingUsers ? 0.6 : 1,
                    cursor: loadingUsers ? "default" : "pointer"
                  }}
                >
                  <span className="permissions-select-text">
                    {loadingUsers
                      ? "Loading users..."
                      : formData.assignedUserIds.length === 0
                        ? "Select assignees"
                        : `${formData.assignedUserIds.length} user${formData.assignedUserIds.length > 1 ? "s" : ""} selected`}
                  </span>
                  <FaChevronDown
                    className={`permissions-select-chevron ${showAssigneesDropdown ? "open" : ""}`}
                  />
                </div>
                {showAssigneesDropdown && !loadingUsers && (
                  <div
                    className="permissions-select-panel"
                    style={{ maxHeight: 220, overflowY: "auto" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <label className="permissions-select-item">
                      <input
                        type="checkbox"
                        checked={allUsersSelected}
                        onChange={handleToggleSelectAllUsers}
                      />
                      <span className="permissions-select-item-label">Select all</span>
                    </label>
                    {users.map((u) => (
                      <label key={u.id} className="permissions-select-item">
                        <input
                          type="checkbox"
                          checked={formData.assignedUserIds.includes(u.id)}
                          onChange={() => handleUserToggle(u.id)}
                        />
                        <span className="permissions-select-item-label">{u.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="form-group addtask-description-full">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="4"
                
              />
            </div>
          </div>
        </div>

        {taskId ? (
          <div style={{ marginTop: 20, marginBottom: 8 }}>
            <TaskFileAttachments
              task={{ Task_Id: Number(taskId), task_id: Number(taskId) }}
              taskId={taskId}
              canUpload={canEditTaskFiles}
              title="Task attachments"
              onRefresh={async () => {
                if (!taskId) return;
                try {
                  await fetchTaskById(taskId);
                } catch (err) {
                  console.error("Error refetching task after file change:", err);
                }
              }}
            />
          </div>
        ) : null}

        <div className="form-actions editcustomer-actions">
          <button type="submit" className="btn-subproject-primary" disabled={submitting}>
            {submitting ? "Updating..." : "Update Task"}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}

export default EditTask;
